<template>
  <v-row no-gutters>
    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <v-card-title>
          <span class="title">{{ formTitle }}</span>
        </v-card-title>

        <v-form v-model="drawer.valid">
          <v-card-text>
            <component
              v-for="(field, index) in formFields"
              :is="`${field.type}-field`"
              :key="index"
              :field="field"
              :value.sync="formData[index]"
            ></component>

            <component
              :is="`${linkField.type}-field`"
              :showPreview="false"
              :field="linkField"
              :value.sync="formData['link']"
            ></component>
          </v-card-text>
        </v-form>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            text
            :disabled="!drawer.valid"
            :loading="drawer.loading"
            @click="save"
          >Save</v-btn>

          <v-btn color="error" text :loading="drawer.loading" @click="closeDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <draggable v-model="menuItems" v-bind="draggableOptions" style="width: 100%">
      <transition-group type="transition" name="flip-list">
        <v-card v-for="item in menuItems" :key="item.id" width="100%" class="mt-4">
          <v-card-text>
            <v-row no-gutters>
              <v-col>
                <div>Name: {{ truncateString(item.name, 25) }}</div>
                <div>
                  Link:
                  <span v-if="item.children.length">Parent link (not clickable)</span>
                  <a
                    v-else
                    :href="item.link"
                    :target="item.type == 'url' ? '_blank' : ''"
                  >{{ truncateString(item.link, 25) }}</a>
                </div>
              </v-col>
              <v-col cols="2">
                <v-layout>
                  <v-spacer></v-spacer>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn v-on="on" icon small color="accent" @click="editMenuItem(item)">
                        <v-icon :fill="$vuetify.theme.defaults.dark.accent" style="width: 18">$edit</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit menu</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn v-on="on" icon small color="error" @click="deleteMenuItem(item.id)">
                        <v-icon>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete menu</span>
                  </v-tooltip>
                </v-layout>
              </v-col>
            </v-row>

            <draggable v-model="item.children" v-bind="draggableOptions" style="width: 100%">
              <v-card v-for="child in item.children" :key="child.id" class="mt-3">
                <v-card-text>
                  <v-row no-gutters>
                    <v-col>
                      <div>Name: {{ truncateString(child.name, 25) }}</div>
                      <div>
                        Link:
                        <a
                          :href="child.link"
                          :target="child.type == 'url' ? '_blank' : ''"
                        >{{ truncateString(child.link, 25) }}</a>
                      </div>
                    </v-col>
                    <v-col cols="2">
                      <v-layout>
                        <v-spacer></v-spacer>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-btn v-on="on" icon small color="accent" @click="editMenuItem(child)">
                              <v-icon
                                :fill="$vuetify.theme.defaults.dark.accent"
                                style="width: 18"
                              >$edit</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit menu</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-btn
                              v-on="on"
                              icon
                              small
                              color="error"
                              @click="deleteMenuItem(child.id)"
                            >
                              <v-icon>mdi-trash-can-outline</v-icon>
                            </v-btn>
                          </template>
                          <span>Delete menu</span>
                        </v-tooltip>
                      </v-layout>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </draggable>
          </v-card-text>
        </v-card>
      </transition-group>
    </draggable>
  </v-row>
</template>

<script>
import { activeTemplate } from '@/graphql/template.gql'
import { pageBySlug } from '@/graphql/page.gql'
import {
  upsertMenuItem,
  deleteMenuItem,
  buildMenuStructure
} from '@/graphql/menu.gql'
import { mapState, mapActions } from 'vuex'
import draggable from 'vuedraggable'

export default {
  name: 'MenuItemsTable',
  props: {
    menuID: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      required: true
    }
  },
  components: {
    draggable
  },
  data: () => ({
    dialog: false,
    formData: {
      id: null,
      name: '',
      type: '',
      link: ''
    },
    defaultFormData: {
      id: null,
      name: '',
      type: '',
      link: ''
    },
    formFields: {
      name: {
        label: 'Name',
        type: 'text',
        rules: ['required']
      },
      type: {
        label: 'Link type',
        type: 'select',
        rules: ['required'],
        options: [
          { value: 'page', text: 'Page' },
          { value: 'section', text: 'Section' },
          { value: 'file', text: 'File' },
          { value: 'url', text: 'URL' }
        ]
      }
    }
  }),
  computed: {
    formTitle() {
      return this.formData.id === null ? 'New menu item' : 'Edit menu item'
    },
    ...mapState('editor', ['drawer']),
    menuItems: {
      get() {
        return this.items ? this.items : []
      },
      set(value) {
        this.buildMenuStructure(value)
      }
    },
    linkField() {
      let field = {
        label: 'Link',
        rules: ['required']
      }

      if (this.formData.type == 'page') {
        field.type = 'select'
        field.options = this.pageOptions
      } else if (this.formData.type == 'section') {
        field.type = 'select'
        field.options = this.sectionOptions
      } else if (this.formData.type == 'file') {
        field.type = 'media-manager'
        field.allowedTypes = ['image', 'video', 'document']
      } else {
        field.type = 'text'
      }

      return field
    },
    pageOptions() {
      return !this.activeTemplate
        ? []
        : this.activeTemplate.pages.map(page => {
            return { value: '/' + page.slug, text: page.name }
          })
    },
    sectionOptions() {
      return !this.pageBySlug
        ? []
        : this.pageBySlug.sections.map(section => {
            return { value: '#' + section.element_id, text: section.element_id }
          })
    },
    draggableOptions() {
      return {
        swapThreshold: 0.5,
        animation: 200,
        group: {
          name: `menuItems${this.menuID}`
        }
      }
    }
  },
  watch: {
    'formData.type'(value) {
      if (
        value == 'page' &&
        !this.pageOptions.filter(item => item.value == this.formData.link)
          .length
      ) {
        this.formData.link = ''
      }

      if (
        value == 'section' &&
        !this.sectionOptions.filter(item => item.value == this.formData.link)
          .length
      ) {
        this.formData.link = ''
      }
    }
  },
  apollo: {
    activeTemplate: {
      query: activeTemplate
    },
    pageBySlug: {
      query: pageBySlug,
      skip() {
        return !this.activeTemplate
      },
      variables() {
        return {
          data: {
            slug:
              this.$route.params.page ||
              this.activeTemplate.pages.filter(page => page.home)[0].slug,
            template_id: this.activeTemplate.id
          }
        }
      }
    }
  },
  methods: {
    ...mapActions('editor', ['saveChanges', 'confirmAndSaveChanges']),
    closeDialog() {
      this.dialog = false
      this.formData = Object.assign({}, this.defaultFormData)
    },
    createMenuItem() {
      this.formData = Object.assign({}, this.defaultFormData)

      this.dialog = true
    },
    save() {
      this.saveChanges({
        mutation: upsertMenuItem,
        variables: {
          data: {
            ...this.formData,
            menu_id: this.menuID
          }
        },
        messages: {
          success: this.formData.id
            ? 'Menu item successfully updated.'
            : 'Menu item successfully created.'
        },
        refetch: true,
        close: false
      })
    },
    editMenuItem(data) {
      this.formData = Object.assign(
        {},
        {
          id: data.id,
          name: data.name,
          type: data.type,
          link: data.link
        }
      )

      this.dialog = true
    },
    deleteMenuItem(id) {
      this.confirmAndSaveChanges({
        mutation: deleteMenuItem,
        variables: { id },
        messages: {
          confirm: 'You will not be able to revert this.',
          success: 'Menu successfully deleted.'
        },
        refetch: true,
        close: false
      })
    },
    buildMenuStructure(items) {
      this.saveChanges({
        mutation: buildMenuStructure,
        variables: {
          data: {
            items,
            menu_id: this.menuID
          }
        },
        messages: {
          success: 'Menu structure successfully updated.'
        },
        refetch: true,
        close: false
      })
    }
  }
}
</script>
